import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IdentificationCard, Plus, ArrowsCounterClockwise, CheckCircle, ListChecks,
  Lightning, X, BookOpen, CalendarBlank, Wheelchair, Buildings, MapPin, MagnifyingGlass,
  ArrowRight, Trash, CaretDown, Bus, Path,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { passengerApi, getPassengerUser } from "@/lib/passengerApi";
import { MajheHeader } from "@/components/majhe/MajheLayout";
import { FieldShell } from "@/pages/majhe/CardHub";
import DocumentUploader from "@/components/majhe/DocumentUploader";
import { useMajheMeta } from "@/lib/useMajheMeta";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const cryptoId = () => Math.random().toString(36).slice(2, 10);

export default function PassHub() {
  const navigate = useNavigate();
  const user = getPassengerUser();
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [showRenew, setShowRenew] = useState(null);
  const [showHistory, setShowHistory] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await passengerApi.get("/me/passes");
      setPasses(data);
    } finally { setLoading(false); }
  };

  const consumeTrip = async (passNo) => {
    try {
      const { data } = await passengerApi.post("/passes/consume-trip", { pass_no: passNo });
      const left = data.trips_left_today === "unlimited" ? "∞" : data.trips_left_today;
      toast.success(`Trip recorded · ${left} trips left today`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trip cap reached");
    }
  };

  useEffect(() => { load(); }, []);

  const activePasses = passes.filter((p) => p.is_valid);
  const expiredPasses = passes.filter((p) => !p.is_valid);

  return (
    <div data-testid="majhe-pass-hub">
      <MajheHeader title="Smart Pass" onBack={() => navigate("/majhe")}/>

      {loading && <div className="px-5"><div className="majhe-skel h-44"/></div>}

      {!loading && passes.length === 0 && (
        <div className="px-5 mt-2">
          <div className="majhe-card text-center py-8">
            <IdentificationCard size={42} className="mx-auto mb-2"/>
            <div className="font-extrabold text-lg">No active pass</div>
            <div className="text-sm opacity-85 mt-1 max-w-xs mx-auto">Get your KTCL Smart Pass for unlimited bus travel within Goa</div>
          </div>
          <button onClick={() => setShowApply(true)} data-testid="pass-apply-empty-btn" className="majhe-btn mt-4">
            <Plus size={18} weight="bold"/> Apply for Smart Pass
          </button>
        </div>
      )}

      {!loading && activePasses.length > 0 && (
        <div className="px-5 mt-1">
          <h3 className="text-[12px] uppercase tracking-wider text-[#7B7390] font-bold mb-2 px-1">Active Pass</h3>
          {activePasses.map((p) => (
            <PassCard key={p.id} p={p} onRenew={() => setShowRenew(p)}
              onHistory={() => setShowHistory(p)} onConsume={() => consumeTrip(p.pass_no)}/>
          ))}
        </div>
      )}

      {!loading && expiredPasses.length > 0 && (
        <div className="px-5 mt-6">
          <h3 className="text-[12px] uppercase tracking-wider text-[#7B7390] font-bold mb-2 px-1">Expired</h3>
          {expiredPasses.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border border-[#EDE8FA] p-4 mb-2.5 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#FEE2E2] text-[#991B1B] flex items-center justify-center">
                <IdentificationCard size={20} weight="fill"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-[#18142A] truncate">{p.pass_no}</div>
                <div className="text-[11px] text-[#7B7390]">{p.category} · expired {p.valid_to}</div>
              </div>
              <button onClick={() => setShowRenew(p)} data-testid="pass-renew-old-btn"
                className="majhe-pill majhe-pill--violet">Renew</button>
            </div>
          ))}
        </div>
      )}

      {!loading && passes.length > 0 && (
        <div className="px-5 mt-6">
          <button onClick={() => setShowApply(true)} data-testid="pass-add-new-btn" className="majhe-btn majhe-btn--ghost">
            <Plus size={18} weight="bold"/> Apply for another pass
          </button>
        </div>
      )}

      {showApply && <ApplySheet onClose={() => setShowApply(false)}
        onDone={(meta) => {
          setShowApply(false);
          if (meta?.navigateTo) { navigate(meta.navigateTo); return; }
          load();
        }} userName={user?.name}/>}
      {showRenew && <RenewSheet pass={showRenew} onClose={() => setShowRenew(null)}
        onDone={() => { setShowRenew(null); load(); }}/>}
      {showHistory && <HistorySheet pass={showHistory} onClose={() => setShowHistory(null)}/>}
    </div>
  );
}

function PassCard({ p, onRenew, onHistory, onConsume }) {
  const unlimited = p.trips_left_today === "unlimited";
  const cap = p.daily_cap === "unlimited" ? null : p.daily_cap;
  const used = p.trips_today;
  const pct = cap ? Math.min(100, (used / cap) * 100) : 0;

  return (
    <div className="majhe-card" data-testid="pass-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider opacity-90 flex items-center gap-1.5">
            {p.category === "Person with Disability" ? <Wheelchair size={14} weight="fill"/> : <IdentificationCard size={14} weight="fill"/>}
            {p.category}
          </div>
          <div className="text-xl font-extrabold mt-1">{p.pass_no}</div>
          <div className="text-[12px] opacity-90 mt-0.5">{p.pass_type} · valid until {p.valid_to}</div>
        </div>
        <span className="majhe-card-btn" style={{ background: unlimited ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.14)" }}>
          {p.days_remaining}d left
        </span>
      </div>

      {/* Daily trip tracker */}
      <div className="mt-4 rounded-xl bg-white/15 border border-white/15 p-3">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider opacity-90">
          <span>Today&apos;s trips</span>
          <span className="font-bold">
            {unlimited ? `${used} used · UNLIMITED` : `${used} / ${cap}`}
          </span>
        </div>
        {!unlimited && (
          <div className="mt-2 h-2 rounded-full bg-white/15 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #C9B5FF 0%, #FFFFFF 100%)" }}/>
          </div>
        )}
        <div className="mt-2 text-[12px] font-bold">
          {unlimited ? "Unlimited trips today" : `${p.trips_left_today} trip${p.trips_left_today === 1 ? "" : "s"} remaining today`}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <button data-testid="pass-consume-trip" onClick={onConsume}
          type="button"
          aria-label={!unlimited && p.trips_left_today === 0
            ? "No trips left today. Daily cap reached"
            : `Use one trip. ${unlimited ? "Unlimited trips available" : p.trips_left_today + " trips remaining today"}`}
          className="majhe-card-btn justify-center"
          disabled={!unlimited && p.trips_left_today === 0}>
          <Lightning size={12} weight="fill" aria-hidden="true"/> Use trip
        </button>
        <button data-testid="pass-renew-btn" onClick={onRenew}
          type="button" aria-label={`Renew pass ${p.pass_no}`}
          className="majhe-card-btn justify-center">
          <ArrowsCounterClockwise size={12} weight="bold" aria-hidden="true"/> Renew
        </button>
        <button data-testid="pass-history-btn" onClick={onHistory}
          type="button" aria-label={`Show transaction history for pass ${p.pass_no}`}
          className="majhe-card-btn justify-center">
          <BookOpen size={12} weight="bold" aria-hidden="true"/> History
        </button>
      </div>
    </div>
  );
}

function ApplySheet({ onClose, onDone, userName }) {
  const { meta } = useMajheMeta();
  const passCategories = meta?.pass_categories || ["General"];
  const passTypes = meta?.pass_types || ["Monthly"];
  const depots = meta?.depots || ["Panaji"];
  const docRequirements = meta?.pass_doc_requirements || {};
  const labels = meta?.category_discount_label || {};
  const busServiceOptions = meta?.bus_services || ["SHUTTLE", "SHUTTLE EV", "LOCAL", "LOCAL EV", "SHUTTLE SPECIAL"];

  const [category, setCategory] = useState("General");
  const [type, setType] = useState("Monthly");
  const [depot, setDepot] = useState("Panaji");
  const [holder, setHolder] = useState(userName || "");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [institutionName, setInstitutionName] = useState("");
  const [docs, setDocs] = useState({});
  const [busy, setBusy] = useState(false);

  // NEW — KTCL-portal-style fields
  const [busServices, setBusServices] = useState(["SHUTTLE"]);
  const [routes, setRoutes] = useState([
    { id: cryptoId(), from_stop: "", to_stop: "", via_stop: "", pass_type: "Monthly",
      distance_km: 0, amount: 0, single_fare: 0, fare_source: "" },
  ]);
  const [pickerFor, setPickerFor] = useState(null); // {idx, target}
  const [consent, setConsent] = useState(false);

  const requiredDocs = docRequirements[category] || [];
  const discountLabel = labels[category] || "";
  const totalAmount = routes.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const totalPayable = totalAmount + 35;

  const setDoc = (key, val) => setDocs((d) => ({ ...d, [key]: val }));

  const updateRoute = (idx, patch) => setRoutes((rs) =>
    rs.map((r, i) => (i === idx ? { ...r, ...patch } : r))
  );

  const addRoute = () => {
    if (routes.length >= 5) { toast.warning("Maximum 5 routes per pass"); return; }
    setRoutes([...routes, {
      id: cryptoId(), from_stop: "", to_stop: "", via_stop: "", pass_type: type,
      distance_km: 0, amount: 0, single_fare: 0, fare_source: "",
    }]);
  };
  const removeRoute = (idx) => {
    if (routes.length === 1) { toast.warning("At least one route is required"); return; }
    setRoutes((rs) => rs.filter((_, i) => i !== idx));
  };

  // Recompute amount per route whenever from/to/pass_type/category changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await Promise.all(routes.map(async (r) => {
        if (!r.from_stop || !r.to_stop) {
          return { ...r, amount: 0, distance_km: 0, single_fare: 0, fare_source: "" };
        }
        try {
          const { data } = await passengerApi.post("/passes/estimate-route", {
            from_stop: r.from_stop, to_stop: r.to_stop,
            via_stop: r.via_stop || "",
            pass_type: r.pass_type || type,
            category,
          });
          return {
            ...r, amount: data.amount, distance_km: data.distance_km,
            single_fare: data.single_fare, fare_source: data.fare_source,
          };
        } catch { return { ...r, amount: 0 }; }
      }));
      if (!cancelled) setRoutes(next);
    })();
    return () => { cancelled = true; };
  }, [
    category,
    routes.map((r) => `${r.from_stop}|${r.to_stop}|${r.pass_type}`).join("·"),
  ]);

  const submit = async () => {
    if (!holder.trim()) { toast.error("Enter holder name"); return; }
    if (busServices.length === 0) { toast.error("Select at least one bus service"); return; }
    if (routes.some((r) => !r.from_stop || !r.to_stop)) {
      toast.error("Pick from & to for every route row"); return;
    }
    if (!consent) { toast.error("Please tick the self-declaration consent"); return; }
    // Client-side validation of required docs
    const missing = requiredDocs
      .filter((d) => d.required && !(docs[d.key] || "").trim())
      .map((d) => d.label);
    if (missing.length) {
      toast.error(`Please upload: ${missing.join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await passengerApi.post("/passes/apply", {
        category, pass_type: type, holder_name: holder,
        depot, address, city, pincode, institution_name: institutionName,
        documents: docs,
        routes: routes.map((r) => ({
          from_stop: r.from_stop, to_stop: r.to_stop,
          via_stop: r.via_stop || "", pass_type: r.pass_type || type,
        })),
        bus_services: busServices,
        consent_signed: consent,
      });
      const hasDocs = Object.keys(docs).length > 0;
      toast.success(hasDocs
        ? `Application ${data.pass_no} submitted · track its status anytime`
        : `Pass issued · ${data.pass_no}`);
      onDone({ navigateTo: hasDocs ? "/majhe/applications" : null });
    } catch (e) { toast.error(e?.response?.data?.detail || "Application failed"); }
    finally { setBusy(false); }
  };

  return (
    <Sheet title="Apply for Smart Pass" onClose={onClose} tid="apply-sheet">
      <div className="space-y-4">
        {/* STEP 1: pick the collection depot FIRST — before payment */}
        <div className="rounded-2xl bg-gradient-to-br from-[#6F2EE8] to-[#2D1170] text-white p-4 shadow-md" data-testid="pass-collection-depot-block">
          <div className="text-[11px] uppercase tracking-wider font-bold opacity-90 flex items-center gap-1.5">
            <Buildings size={13} weight="bold"/> Step 1 · Collection Depot
          </div>
          <div className="text-[13px] mt-1.5 opacity-95">
            Choose where you want to pick up your physical Smart Pass. KTCL will keep it ready at the counter after documents are verified.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            {depots.map((d) => (
              <button key={d} type="button" data-testid={`apply-pass-depot-${d}`}
                onClick={() => setDepot(d)}
                className={`px-3 py-2 rounded-xl text-[12.5px] font-bold border transition-all ${
                  depot === d
                    ? "bg-white text-[#2D1170] border-white shadow-md"
                    : "bg-white/10 text-white border-white/30 hover:bg-white/20"
                }`}>
                {d} KTC
              </button>
            ))}
          </div>
        </div>

        {/* Holder */}
        <FieldShell label="Holder Name">
          <input className="majhe-input" data-testid="apply-pass-name" style={{ paddingLeft: 18 }}
            placeholder="Name as per Aadhar" value={holder} onChange={(e) => setHolder(e.target.value)}/>
        </FieldShell>

        {/* Category & Validity */}
        <div className="grid grid-cols-2 gap-3">
          <FieldShell label={`Category${discountLabel ? ` · ${discountLabel}` : ""}`}>
            <select className="majhe-input" data-testid="apply-pass-cat" style={{ paddingLeft: 14, appearance: "auto" }}
              value={category} onChange={(e) => { setCategory(e.target.value); setDocs({}); }}>
              {passCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </FieldShell>
          <FieldShell label="Validity">
            <select className="majhe-input" data-testid="apply-pass-type" style={{ paddingLeft: 14, appearance: "auto" }}
              value={type} onChange={(e) => {
                setType(e.target.value);
                setRoutes((rs) => rs.map((r) => ({ ...r, pass_type: e.target.value })));
              }}>
              {passTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </FieldShell>
        </div>

        {/* STEP 2 — Bus Service multi-select (KTCL portal: SHUTTLE / SHUTTLE EV / LOCAL / LOCAL EV / SHUTTLE SPECIAL) */}
        <div className="rounded-2xl bg-white border border-[#EDE8FA] p-4" data-testid="pass-bus-service-block">
          <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold flex items-center gap-1.5 mb-2">
            <Bus size={13} weight="bold"/> Step 2 · Bus Service <span className="text-rose-500 font-extrabold">*</span>
          </div>
          <div className="text-[11.5px] text-[#5B4E7A] mb-2">
            Pick the KTCL service classes your pass should be valid on.
          </div>
          <div className="flex flex-wrap gap-2">
            {busServiceOptions.map((s) => {
              const active = busServices.includes(s);
              return (
                <button key={s} type="button"
                  onClick={() => setBusServices((arr) =>
                    arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s]
                  )}
                  data-testid={`apply-pass-bus-${s.replace(/\s+/g, "-")}`}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-bold border transition-all ${
                    active
                      ? "bg-[#5B2EE5] text-white border-[#5B2EE5]"
                      : "bg-white text-[#5B2EE5] border-[#DCC9FF] hover:bg-[#F5F2FF]"
                  }`}>
                  {active ? "✓ " : ""}{s}
                </button>
              );
            })}
          </div>
        </div>

        {/* STEP 3 — Pass Routes table (KTCL portal style — up to 5 routes per pass) */}
        <div className="rounded-2xl bg-white border border-[#EDE8FA] p-4 space-y-3" data-testid="pass-routes-block">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold flex items-center gap-1.5">
                <Path size={13} weight="bold"/> Step 3 · Pass Routes <span className="text-rose-500 font-extrabold">*</span>
              </div>
              <div className="text-[11.5px] text-[#5B4E7A] mt-0.5">
                Pick the route(s) for which this pass is valid. Amount auto-computed from GTFS fare.
              </div>
            </div>
            <button type="button" onClick={addRoute} disabled={routes.length >= 5}
              data-testid="apply-pass-add-route"
              className="px-3 py-1.5 rounded-lg text-[11.5px] font-bold bg-emerald-600 text-white shadow-sm disabled:opacity-40 flex items-center gap-1">
              <Plus size={12} weight="bold"/> Add Row
            </button>
          </div>

          {routes.map((r, idx) => (
            <div key={r.id} className="rounded-xl border border-[#E9DFFB] bg-[#FAF7FF] p-3 space-y-2"
              data-testid={`pass-route-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10.5px] uppercase tracking-wider text-[#5B2EE5] font-extrabold">
                  Route #{idx + 1}
                </span>
                {routes.length > 1 && (
                  <button type="button" onClick={() => removeRoute(idx)}
                    data-testid={`pass-route-remove-${idx}`}
                    className="w-7 h-7 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center">
                    <Trash size={12} weight="bold"/>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <RouteStopButton label="From" value={r.from_stop} tid={`pass-route-from-${idx}`}
                  onClick={() => setPickerFor({ idx, target: "from_stop", placeholder: "From bus stop" })}/>
                <RouteStopButton label="To" value={r.to_stop} tid={`pass-route-to-${idx}`}
                  onClick={() => setPickerFor({ idx, target: "to_stop", placeholder: "To bus stop" })}/>
              </div>

              <RouteStopButton label="Via (optional)" value={r.via_stop} tid={`pass-route-via-${idx}`}
                optional
                onClick={() => setPickerFor({ idx, target: "via_stop", placeholder: "Via bus stop (optional)" })}/>

              <div className="grid grid-cols-3 gap-2 text-[11.5px]">
                <div className="text-center bg-white border border-[#E9DFFB] rounded-lg py-1.5">
                  <div className="text-[9px] uppercase text-[#7B7390] font-bold">Distance</div>
                  <div className="font-extrabold text-[#18142A]">{r.distance_km ? `${r.distance_km} km` : "—"}</div>
                </div>
                <div className="text-center bg-white border border-[#E9DFFB] rounded-lg py-1.5">
                  <div className="text-[9px] uppercase text-[#7B7390] font-bold">Single Fare</div>
                  <div className="font-extrabold text-[#18142A]">{r.single_fare ? `₹${r.single_fare}` : "—"}</div>
                </div>
                <div className="text-center bg-[#FDF7E3] border border-[#F4E4A3] rounded-lg py-1.5">
                  <div className="text-[9px] uppercase text-[#7A5106] font-bold">Amount</div>
                  <div className="font-extrabold text-[#7A5106]" data-testid={`pass-route-amount-${idx}`}>
                    {r.amount ? `₹${r.amount}` : "—"}
                  </div>
                </div>
              </div>
              {r.fare_source === "distance" && (
                <div className="text-[10.5px] text-amber-700">⚠ Distance-based estimate (no official GTFS fare for this pair).</div>
              )}
            </div>
          ))}
        </div>

        {/* Student-only institution field */}
        {category === "Student" && (
          <FieldShell label="Institution / College Name" icon={<Buildings size={18} weight="bold"/>}>
            <input className="majhe-input" data-testid="apply-pass-institution"
              placeholder="Goa Engineering College"
              value={institutionName} onChange={(e) => setInstitutionName(e.target.value)}/>
          </FieldShell>
        )}

        {/* Address fields */}
        <FieldShell label="Address" icon={<MapPin size={18} weight="bold"/>}>
          <input className="majhe-input" data-testid="apply-pass-addr"
            placeholder="House no, Street, Area"
            value={address} onChange={(e) => setAddress(e.target.value)}/>
        </FieldShell>
        <div className="grid grid-cols-2 gap-3">
          <FieldShell label="City / Town">
            <input className="majhe-input" data-testid="apply-pass-city" style={{ paddingLeft: 18 }}
              placeholder="Panaji" value={city} onChange={(e) => setCity(e.target.value)}/>
          </FieldShell>
          <FieldShell label="PIN Code">
            <input className="majhe-input" data-testid="apply-pass-pin" style={{ paddingLeft: 18 }}
              placeholder="403001" inputMode="numeric" maxLength={6}
              value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}/>
          </FieldShell>
        </div>

        {/* Documents */}
        {requiredDocs.length > 0 && (
          <div className="pt-2">
            <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold mb-2 px-1">
              Documents Required ({category})
            </div>
            <div className="space-y-3">
              {requiredDocs.map((d) => (
                <DocumentUploader key={d.key}
                  label={d.label} required={d.required}
                  value={docs[d.key] || ""}
                  onChange={(v) => setDoc(d.key, v)}
                  tid={`apply-pass-doc-${d.key}`}/>
              ))}
            </div>
          </div>
        )}

        {/* Registration consent (per KTCL portal — self declaration) */}
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="pass-consent-block">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="apply-pass-consent"
              className="mt-0.5 w-4 h-4 accent-[#5B2EE5]"/>
            <div className="text-[11.5px] leading-snug text-amber-900">
              I, <b>{holder || "Passenger"}</b>, do hereby declare that I am not an employee of Central or State Government,
              nor of any Central/State Government Autonomous Body, nor of any State Government Aided
              Institution/Organisation. I am aware that this Pass Scheme is not eligible to employees of any
              State/Central Government Departments/Organisations. I attach a self-attested photocopy
              (Aadhar Card) of my Identity Proof for record.
            </div>
          </label>
        </div>

        {/* Price summary */}
        <div className="rounded-xl bg-[#F5F2FF] border border-[#DCC9FF] text-[#4920C2] p-3 text-[12px]" data-testid="apply-pass-charges">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-80 mb-2">Step 4 · Payment summary</div>
          {routes.map((r, idx) => (
            (r.from_stop && r.to_stop) && (
              <div key={r.id} className="flex items-center justify-between mb-1 text-[11.5px]">
                <span className="truncate pr-2">Route {idx + 1} · {r.from_stop} → {r.to_stop}</span>
                <span className="font-bold tabular-nums">₹{r.amount || 0}</span>
              </div>
            )
          ))}
          <div className="flex items-center justify-between border-t border-[#DCC9FF] pt-2 mt-1">
            <span>Registration Fee</span>
            <span className="font-bold">₹35</span>
          </div>
          <div className="flex items-center justify-between border-t border-[#DCC9FF] pt-2 mt-1">
            <span className="font-extrabold">Total to Pay</span>
            <span className="font-extrabold text-[#2D1170] text-base" data-testid="apply-pass-total">
              ₹{totalPayable}
            </span>
          </div>
          <div className="text-[11px] mt-2 opacity-90">
            {discountLabel && <>{category} concession · {discountLabel}. </>}
            Collect your physical pass from <b>{depot} KTC counter</b> after document verification.
          </div>
        </div>
      </div>

      {/* Stop picker overlay — reuses the BookBus pattern */}
      {pickerFor && (
        <PassStopPicker target={pickerFor}
          onClose={() => setPickerFor(null)}
          onPick={(name) => {
            updateRoute(pickerFor.idx, { [pickerFor.target]: name });
            setPickerFor(null);
          }}/>
      )}

      <button onClick={submit} disabled={busy} data-testid="apply-pass-submit"
        className="majhe-btn mt-5">{busy ? "Submitting…" : "Apply & Submit"}</button>
    </Sheet>
  );
}

function RenewSheet({ pass, onClose, onDone }) {
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await passengerApi.post("/passes/renew", { pass_no: pass.pass_no, days });
      toast.success(`Renewed · valid until ${data.valid_to}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Sheet title="Renew Pass" onClose={onClose} tid="renew-sheet">
      <div className="bg-[#F5F2FF] rounded-xl p-3 mb-4">
        <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold">Pass</div>
        <div className="font-bold text-[#18142A]">{pass.pass_no}</div>
        <div className="text-[11px] text-[#7B7390]">{pass.category} · {pass.pass_type}</div>
      </div>
      <FieldShell label={`Extend by · ${days} days`}>
        <div className="grid grid-cols-4 gap-1.5 mt-1 mb-2">
          {[30, 90, 180, 365].map((d) => (
            <button key={d} onClick={() => setDays(d)} type="button" data-testid={`renew-days-${d}`}
              className={`py-2 rounded-lg text-xs font-bold transition-colors ${
                days === d ? "bg-[#5B2EE5] text-white" : "bg-[#F5F2FF] text-[#5B2EE5]"
              }`}>
              {d === 30 ? "1 mo" : d === 90 ? "3 mo" : d === 180 ? "6 mo" : "1 yr"}
            </button>
          ))}
        </div>
      </FieldShell>
      <button onClick={submit} disabled={busy} data-testid="renew-submit"
        className="majhe-btn mt-2">{busy ? "Renewing…" : "Renew Pass"}</button>
    </Sheet>
  );
}

function HistorySheet({ pass, onClose }) {
  const history = pass.history || [];
  return (
    <Sheet title="Pass History" onClose={onClose} tid="pass-history-sheet">
      <div className="bg-[#F5F2FF] rounded-xl p-3 mb-4">
        <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold">{pass.holder_name}</div>
        <div className="font-bold text-[#18142A]">{pass.pass_no}</div>
        <div className="grid grid-cols-3 gap-2 mt-2 text-center">
          <Stat label="Total Paid" value={`₹${pass.total_paid}`}/>
          <Stat label="Renewals" value={pass.renew_count || 0}/>
          <Stat label="Trips Used" value={(pass.trips_used || []).length}/>
        </div>
      </div>
      <h3 className="text-[16px] font-extrabold text-[#18142A] mb-1">Transactions</h3>
      {history.length === 0 && <div className="text-center py-6 text-[#7B7390] text-sm">No transactions yet</div>}
      {history.map((h, i) => (
        <div key={i} className="majhe-hist-row" data-testid="pass-hist-row">
          <div className="majhe-hist-row__icon"><IdentificationCard size={20} weight="fill"/></div>
          <div>
            <div className="majhe-hist-row__title">{h.kind || "Transaction"}</div>
            <div className="majhe-hist-row__sub">{h.days ? `${h.days} days extension` : "Pass issued"}</div>
            <div className="majhe-hist-row__date">{new Date(h.at).toLocaleString()}</div>
          </div>
          <div className="majhe-hist-row__amount">₹ {Number(h.amount || 0).toFixed(2)}</div>
        </div>
      ))}
    </Sheet>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-lg p-2">
      <div className="text-[10px] uppercase tracking-wider text-[#7B7390] font-semibold">{label}</div>
      <div className="text-sm font-extrabold text-[#18142A]">{value}</div>
    </div>
  );
}

function Sheet({ title, onClose, tid, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end" style={{ background: "rgba(15,8,40,0.55)" }}
      onClick={onClose} data-testid={tid}
      role="dialog" aria-modal="true" aria-labelledby={`${tid}-title`}>
      <div className="bg-white w-full rounded-t-[28px] overflow-hidden" style={{ maxHeight: "92dvh" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#F0EBFC] flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 id={`${tid}-title`} className="text-lg font-extrabold text-[#18142A]">{title}</h2>
          <button onClick={onClose} type="button" aria-label={`Close ${title.toLowerCase()}`}
            className="text-[#7B7390] font-bold text-2xl leading-none px-2"><X size={22} aria-hidden="true"/></button>
        </div>
        <div className="overflow-y-auto p-5 majhe-sheet-body" style={{ maxHeight: "75dvh" }}>{children}</div>
      </div>
    </div>
  );
}


function RouteStopButton({ label, value, onClick, tid, optional }) {
  return (
    <button type="button" onClick={onClick} data-testid={tid}
      className="w-full text-left bg-white border border-[#E9DFFB] rounded-lg px-3 py-2 hover:border-[#5B2EE5] transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#7B7390] font-bold">
            {label} {optional && <span className="opacity-60 normal-case font-normal">(opt.)</span>}
          </div>
          <div className={`text-[13px] font-bold ${value ? "text-[#18142A]" : "text-[#B7AFCC]"} truncate`}>
            {value || "Select a stop"}
          </div>
        </div>
        <CaretDown size={14} weight="bold" className="text-[#7B7390] shrink-0 ml-2"/>
      </div>
    </button>
  );
}

function PassStopPicker({ target, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${PUBLIC_API}/stops/search?q=${encodeURIComponent(q)}&limit=60`);
        const data = await res.json();
        setStops(data);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 200); }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-end" style={{ background: "rgba(15,8,40,0.55)" }}
      onClick={onClose} data-testid="pass-stop-picker"
      role="dialog" aria-modal="true">
      <div className="bg-white w-full rounded-t-[28px] overflow-hidden flex flex-col"
        style={{ maxHeight: "85dvh" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-3 border-b border-[#F0EBFC] sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold">
                {target.target.replace("_stop", "")}
              </div>
              <h2 className="text-lg font-extrabold text-[#18142A]">
                {target.placeholder}
              </h2>
            </div>
            <button onClick={onClose} type="button" aria-label="Close"
              className="w-9 h-9 rounded-lg bg-slate-100 text-[#7B7390]"><X size={20}/></button>
          </div>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7B7390]"/>
            <input ref={inputRef} data-testid="pass-stop-search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Type bus stop name…"
              className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-full text-sm font-medium outline-none focus:border-[#5B2EE5] focus:bg-white"/>
          </div>
        </div>
        <div className="overflow-y-auto p-3 flex-1 majhe-sheet-body">
          {loading && (
            <div className="space-y-2 p-2">
              {[0, 1, 2, 3, 4].map((i) => <div key={i} className="majhe-skel h-12"/>)}
            </div>
          )}
          {!loading && stops.length === 0 && (
            <div className="text-center text-[#7B7390] text-sm py-12">No bus stops match &quot;{q}&quot;</div>
          )}
          {!loading && stops.map((s) => (
            <button key={s.id} type="button"
              data-testid={`pass-stop-pick-${s.id}`}
              onClick={() => onPick(s.name)}
              className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-[#F5F2FF] flex items-center gap-2.5">
              <MapPin size={16} className="text-[#5B2EE5]"/>
              <div className="flex-1">
                <div className="text-[13px] font-bold text-[#18142A]">{s.name}</div>
                {s.code && <div className="text-[10.5px] text-[#7B7390]">Code · {s.code}</div>}
              </div>
              <ArrowRight size={14} className="text-[#B7AFCC]"/>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
